import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';

import { DataTable } from '@/components/DataTable/DataTable';
import DetailDrawer from '@/components/DetailDrawer/DetailDrawer';
import GuidancePanel, { GUIDANCE } from '@/components/GuidancePanel';
import {
  ATTESTATION_STATUS_LABELS,
  type AttestationStatus,
} from '@/types/domain';
import type { AttestationId } from '@/types/ids';

import type {
  AttestationRow,
  AttestationsApi,
  AttestationsQuery,
} from './api';
import { createFetchAttestationsApi } from './api.fetch';
import './Attestations.css';

interface AttestationsProps {
  api?: AttestationsApi;
}

const DEFAULT_API: AttestationsApi = createFetchAttestationsApi();

const STATUS_FILTERS: Array<{ key: string; label: string; status?: AttestationStatus }> = [
  { key: 'all', label: 'All' },
  { key: 'PARTIAL', label: 'Partial', status: 'PARTIAL' },
  { key: 'PENDING', label: 'Pending', status: 'PENDING' },
  { key: 'SEALED', label: 'Sealed', status: 'SEALED' },
  { key: 'EXPIRED', label: 'Expired', status: 'EXPIRED' },
];

export default function Attestations({ api = DEFAULT_API }: AttestationsProps) {
  const [status, setStatus] = useState<AttestationStatus | undefined>(undefined);
  const [verified, setVerified] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<AttestationRow | null>(null);
  const [sealOpen, setSealOpen] = useState(false);
  const [toDelete, setToDelete] = useState<AttestationRow | null>(null);
  const [toast, setToast] = useState<string>('');
  const [revoked, setRevoked] = useState<Set<string>>(new Set());
  const [extras, setExtras] = useState<AttestationRow[]>([]);

  const flashToast = (text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(''), 3500);
  };

  const commitSeal = (draft: SealDraft) => {
    const id = `local-att-${Date.now().toString(36)}`;
    const now = new Date().toISOString();
    const receipt = `0x${Math.floor(Math.random() * 0xffff).toString(16).padStart(4, '0').toUpperCase()}`;
    const row: AttestationRow = {
      attestationId: id as unknown as AttestationId,
      receipt,
      scope: draft.scope,
      collected: draft.signers.length,
      quorum: 3,
      status: draft.signers.length >= 3 ? 'SEALED' : 'PARTIAL',
      sealedAt: draft.signers.length >= 3 ? now : null,
      expiresAt: null,
      merkleRoot: `${receipt.toLowerCase()}${Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, '0')}…`,
      signatures: draft.signers.map((label, idx) => ({
        signerId: `local-signer-${idx}` as unknown as AttestationRow['signatures'][number]['signerId'],
        signerLabel: label,
        role: 'ComplianceReviewer',
        signedAt: now,
      })),
    };
    setExtras((e) => [row, ...e]);
    flashToast(`Attestation ${receipt} sealed with ${draft.signers.length} of 3 signatures.`);
    setSealOpen(false);
  };

  const commitRevoke = () => {
    if (!toDelete) return;
    setRevoked((r) => {
      const next = new Set(r);
      next.add(toDelete.attestationId as unknown as string);
      return next;
    });
    flashToast(`Attestation ${toDelete.receipt} revoked.`);
    setToDelete(null);
  };

  const downloadRow = async (row: AttestationRow) => {
    try {
      const blob = new Blob([JSON.stringify(row, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attestation-${row.receipt}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      flashToast(`Downloaded attestation-${row.receipt}.json`);
    } catch (err) {
      flashToast(`Download failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const query: AttestationsQuery = useMemo(
    () => ({ ...(status && { status }) }),
    [status],
  );

  const summaryQ = useQuery({
    queryKey: ['attestations', 'summary'],
    queryFn: () => api.getSummary(),
  });

  const listQ = useQuery({
    queryKey: ['attestations', 'list', query],
    queryFn: () => api.listAttestations(query),
  });

  const verifyMut = useMutation({
    mutationFn: (id: AttestationId) => api.verify(id),
    onSuccess: (result, id) =>
      setVerified((prev) => ({ ...prev, [id as unknown as string]: result.verifiedAt })),
  });

  const rows: AttestationRow[] = useMemo(() => {
    const server = listQ.data ?? [];
    const filtered = server.filter((r) => !revoked.has(r.attestationId as unknown as string));
    return [...extras, ...filtered];
  }, [listQ.data, extras, revoked]);
  const summary = summaryQ.data;
  const queueRow = rows.find((r) => r.status === 'PARTIAL' || r.status === 'PENDING');

  const columns = useMemo<ColumnDef<AttestationRow, unknown>[]>(
    () => [
      {
        id: 'receipt',
        header: 'Receipt',
        cell: (ctx) => <span className="mono">{ctx.row.original.receipt}</span>,
      },
      {
        id: 'scope',
        header: 'Scope',
        cell: (ctx) => <strong>{ctx.row.original.scope}</strong>,
      },
      {
        id: 'quorum',
        header: 'Quorum',
        cell: (ctx) => (
          <span className="mono">
            {ctx.row.original.collected}/{ctx.row.original.quorum}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (ctx) => renderStatusChip(ctx.row.original.status),
      },
      {
        id: 'sealedAt',
        header: 'Sealed at',
        cell: (ctx) => (
          <span className="mono attestations-receipt">
            {ctx.row.original.sealedAt ? ctx.row.original.sealedAt.slice(0, 16) + 'Z' : '—'}
          </span>
        ),
      },
      {
        id: 'merkleRoot',
        header: 'Merkle root',
        cell: (ctx) => (
          <span className="mono attestations-merkle">
            {ctx.row.original.merkleRoot ?? '—'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: (ctx) => {
          const row = ctx.row.original;
          const verifiedAt = verified[row.attestationId as unknown as string];
          return (
            <div className="attestations-actions">
              {row.status === 'SEALED' && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  data-testid={`attestations-verify-${row.receipt}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    verifyMut.mutate(row.attestationId);
                  }}
                >
                  {verifiedAt ? '✓ Verified' : 'Verify'}
                </button>
              )}
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                data-testid={`attestations-download-${row.receipt}`}
                onClick={(e) => {
                  e.stopPropagation();
                  void downloadRow(row);
                }}
              >
                Download
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm btn-danger"
                data-testid={`attestations-revoke-${row.receipt}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setToDelete(row);
                }}
              >
                Revoke
              </button>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable closures
    [verified, verifyMut],
  );

  return (
    <div>
      <PageHead onSeal={() => setSealOpen(true)} />

      <GuidancePanel screenKey="attestations" {...GUIDANCE.attestations} />

      <div className="attestations-kpi-grid" data-testid="attestations-kpi">
        <KpiCard label="PENDING" value={summary?.pending} warn />
        <KpiCard label="SIGNED · 30D" value={summary?.signedLast30d} />
        <KpiCard
          label="AVG TIME TO SEAL"
          value={summary?.avgSealHours}
          suffix="h"
        />
      </div>

      <section className="card">
        <header className="card-head">
          <div className="eyebrow">QUEUE</div>
          <div className="tab-pill-row">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`tab-pill${(f.status ?? undefined) === status ? ' active' : ''}`}
                onClick={() => setStatus(f.status)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </header>

        {queueRow && <QueueHeader row={queueRow} />}

        <div className="attestations-filter-row">
          <label>
            Status
            <select
              aria-label="Filter by attestation status"
              value={status ?? ''}
              onChange={(e) =>
                setStatus((e.target.value || undefined) as AttestationStatus | undefined)
              }
            >
              <option value="">Any status</option>
              {(Object.keys(ATTESTATION_STATUS_LABELS) as AttestationStatus[]).map((s) => (
                <option key={s} value={s}>
                  {ATTESTATION_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <DataTable
          data={rows}
          columns={columns}
          ariaLabel="Attestations"
          caption="Attestations"
          testId="attestations-table"
          loading={listQ.isLoading}
          error={listQ.error}
          emptyMessage="No attestations match the current filter."
          getRowId={(row) => row.attestationId as string}
          onRowClick={(row) => setSelected(row.original)}
        />
        {verifyMut.isSuccess && (
          <div className="attestations-verify-status" role="status">
            Last verify completed at {verifyMut.data.verifiedAt}.
          </div>
        )}
      </section>

      {selected && (
        <DetailDrawer
          testId="attestations-drawer"
          title={`Attestation ${selected.receipt}`}
          subtitle={`${selected.scope} · ${selected.collected}/${selected.quorum} signed`}
          payload={selected}
          onClose={() => setSelected(null)}
        />
      )}

      {sealOpen && (
        <SealAttestationModal onCancel={() => setSealOpen(false)} onSubmit={commitSeal} />
      )}

      {toDelete && (
        <RevokeAttestationModal
          row={toDelete}
          onCancel={() => setToDelete(null)}
          onConfirm={commitRevoke}
        />
      )}

      {toast && (
        <div className="attestations-toast" role="status" aria-live="polite">
          {toast}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Seal + revoke modals
// ---------------------------------------------------------------------------

interface SealDraft {
  scope: string;
  signers: string[];
}

const SIGNER_ROSTER: string[] = [
  'mlro@northstar-bank.co.uk',
  'audit.lead@northstar-bank.co.uk',
  'compliance.2@northstar-bank.co.uk',
  'compliance.3@northstar-bank.co.uk',
  'cro@northstar-bank.co.uk',
];

function SealAttestationModal({
  onCancel,
  onSubmit,
}: {
  onCancel: () => void;
  onSubmit: (draft: SealDraft) => void;
}) {
  const [scope, setScope] = useState('');
  const [signers, setSigners] = useState<Set<string>>(new Set());
  const toggleSigner = (s: string) => {
    setSigners((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };
  const valid = scope.trim().length > 3 && signers.size >= 3;
  return (
    <div
      className="attestations-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Seal new attestation"
      data-testid="attestations-seal-modal"
      onClick={onCancel}
    >
      <div className="attestations-modal" onClick={(e) => e.stopPropagation()}>
        <header>
          <div className="eyebrow">§ SEAL ATTESTATION</div>
          <h2 className="serif" style={{ margin: '6px 0 0', fontSize: 22 }}>
            New attestation — 3-of-5 quorum
          </h2>
          <p style={{ margin: '8px 0 0', color: 'var(--slate-500)', fontSize: 13 }}>
            Pick the scope and ≥ 3 signers. An ephemeral demo signer set is used
            (ADR-0009 — not production keys).
          </p>
        </header>
        <form
          className="attestations-modal-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) onSubmit({ scope: scope.trim(), signers: Array.from(signers) });
          }}
        >
          <label>
            Scope
            <input
              required
              autoFocus
              value={scope}
              placeholder="e.g. Q2 partial audit · trade-based laundering"
              onChange={(e) => setScope(e.target.value)}
              data-testid="attestations-seal-scope"
            />
          </label>
          <fieldset className="attestations-modal-signers">
            <legend>Signers ({signers.size}/3 minimum)</legend>
            {SIGNER_ROSTER.map((s) => (
              <label key={s} className="attestations-modal-signer">
                <input
                  type="checkbox"
                  checked={signers.has(s)}
                  onChange={() => toggleSigner(s)}
                />
                <span className="mono">{s}</span>
              </label>
            ))}
          </fieldset>
          <div className="attestations-modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!valid}
              data-testid="attestations-seal-submit"
            >
              Seal attestation
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RevokeAttestationModal({
  row,
  onCancel,
  onConfirm,
}: {
  row: AttestationRow;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      className="attestations-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Revoke attestation"
      data-testid="attestations-revoke-modal"
      onClick={onCancel}
    >
      <div className="attestations-modal" onClick={(e) => e.stopPropagation()}>
        <header>
          <div className="eyebrow">§ REVOKE ATTESTATION</div>
          <h2 className="serif" style={{ margin: '6px 0 0', fontSize: 22 }}>
            Revoke {row.receipt}?
          </h2>
        </header>
        <div className="attestations-modal-body">
          <p>
            Revoke attestation <strong>{row.receipt}</strong> ({row.scope})? The signed
            artifact itself remains in the audit trail; this flips the active status
            so downstream consumers see it as revoked.
          </p>
        </div>
        <div className="attestations-modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-danger"
            onClick={onConfirm}
            data-testid="attestations-revoke-confirm"
          >
            Revoke
          </button>
        </div>
      </div>
    </div>
  );
}

function PageHead({ onSeal }: { onSeal: () => void }) {
  return (
    <div className="page-head">
      <div>
        <span className="eyebrow">§ ATTESTATIONS</span>
        <h1>Attestations</h1>
        <p style={{ fontSize: 14, color: 'var(--slate-500)', margin: '8px 0 0', maxWidth: 720 }}>
          Evidence flow: a replay run emits an outcome manifest, the manifest is hashed
          into a SignedArtifact, three of five HSM-anchored signers attest to it, and the
          bundle is sealed as an Attestation (ADR-0009). Verify to re-check the signatures
          against the live SignerSet; Download to pull the raw JSON for your own audit
          chain. Sealing new attestations happens on the Audit Packages screen.
        </p>
      </div>
      <button
        type="button"
        className="btn btn-primary"
        onClick={onSeal}
        data-testid="attestations-new"
      >
        + Seal attestation
      </button>
    </div>
  );
}

interface KpiCardProps {
  label: string;
  value: number | undefined;
  suffix?: string;
  warn?: boolean;
}

function KpiCard({ label, value, suffix, warn }: KpiCardProps) {
  return (
    <div className="card attestations-kpi-card">
      <div className="eyebrow">{label}</div>
      <div
        className={`serif attestations-kpi-value${warn ? ' attestations-kpi-value--warn' : ''}`}
      >
        {typeof value === 'number' ? value : '—'}
        {suffix && <span className="attestations-kpi-suffix">{suffix}</span>}
      </div>
    </div>
  );
}

interface QueueHeaderProps {
  row: AttestationRow;
}

function QueueHeader({ row }: QueueHeaderProps) {
  return (
    <div className="attestations-queue" data-testid="attestations-queue">
      <div className="attestations-queue-head">
        <div className="mono" style={{ fontSize: 13 }}>
          {row.receipt} · {row.scope}
        </div>
        <span className="chip warn">
          <span className="dot" />
          {row.collected}/{row.quorum} SIGNED
          {row.expiresAt ? ' · EXPIRES 4h 12m' : ''}
        </span>
      </div>
      <div className="attestations-sigchips">
        {row.signatures.map((sig) => {
          const signed = sig.signedAt !== null;
          return (
            <div
              key={sig.signerId as string}
              className={`attestations-sigchip${signed ? '' : ' attestations-sigchip--pending'}`}
            >
              {signed ? (
                <span className="attestations-sigchip__tick">✓</span>
              ) : (
                <span className="attestations-sigchip__pending">◯</span>
              )}
              {sig.signerLabel} · {sig.role}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function renderStatusChip(status: AttestationStatus) {
  const cls =
    status === 'PARTIAL' || status === 'PENDING'
      ? 'chip warn'
      : status === 'EXPIRED'
        ? 'chip fail'
        : 'chip';
  return (
    <span className={cls}>
      <span className="dot" />
      {ATTESTATION_STATUS_LABELS[status].toUpperCase()}
    </span>
  );
}
